/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type { IntakePerson } from "../api/referrals";
import { StateSelect } from "./StateSelect";
import { Input } from "./ui/Input";
import { blankToNull } from "../lib/utils";

export type IntakeRole = "child" | "caregiver";

/** The person as typed; a space between two names must survive each keystroke. */
function emptyToNull(value: string): string | null {
  return value === "" ? null : value;
}

/** The person as sent: every fact trimmed, blanks as null. */
export function cleanPerson(person: IntakePerson): IntakePerson {
  return {
    firstName: blankToNull(person.firstName),
    lastName: blankToNull(person.lastName),
    email: blankToNull(person.email),
    phoneNumber: blankToNull(person.phoneNumber),
    dob: blankToNull(person.dob),
    stateCode: blankToNull(person.stateCode),
  };
}

/**
 * The person facts a referral carries, as staff enter or correct them: a child has a date of
 * birth, a caregiver has a phone and an email, both have a name and a state. Field errors from
 * nexus arrive as `child.dob`, `caregiver.email`, and paint beside their field.
 */
export function IntakePersonFields({
  role,
  person,
  onChange,
  fieldError,
  stateOptional = false,
}: {
  role: IntakeRole;
  person: IntakePerson;
  onChange: (next: IntakePerson, field: string) => void;
  fieldError: (field: string) => string;
  stateOptional?: boolean;
}) {
  const field = (key: keyof IntakePerson, label: string, type = "text") => (
    <Input
      label={label}
      type={type}
      value={person[key] ?? ""}
      error={fieldError(`${role}.${key}`)}
      onChange={(event) =>
        onChange({ ...person, [key]: emptyToNull(event.target.value) }, `${role}.${key}`)
      }
    />
  );
  const stateError = fieldError(`${role}.stateCode`);
  return (
    <section className="space-y-3">
      <h3 className="font-display text-base text-text-primary">
        {role === "child" ? "Child" : "Caregiver"}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("firstName", "First name")}
        {field("lastName", "Last name")}
        {role === "child" ? (
          field("dob", "Date of birth (YYYY-MM-DD)")
        ) : (
          <>
            {field("phoneNumber", "Phone", "tel")}
            {field("email", "Email", "email")}
          </>
        )}
        <div>
          <StateSelect
            label="State"
            value={person.stateCode ?? ""}
            allowNone={stateOptional}
            onChange={(value) =>
              onChange({ ...person, stateCode: value === "" ? null : value }, `${role}.stateCode`)
            }
          />
          {stateError !== "" && (
            <p className="mt-1.5 text-sm font-medium text-error">{stateError}</p>
          )}
        </div>
      </div>
    </section>
  );
}
